/**
 * 3D wireframe-cube loading spinner rendered on a tiny WebGPU canvas.
 *
 * Replaces the old SVG spinner with a rotating cube that matches the
 * professional 3D-tool aesthetic of the rest of the app.
 *
 * Variants:
 *   - "dark"    – light wireframe on transparent bg (for dark surfaces)
 *   - "light"   – dark wireframe on transparent bg (for light surfaces)
 *   - "rainbow" – edges cycle through full-spectrum hue on transparent bg
 */

import { useEffect, useRef } from "react";
import { hue, mix, sin, time, color as tslColor, uniform, uv } from "three/tsl";
import * as THREE from "three/webgpu";

// ---------------------------------------------------------------------------
// Face-fill palette — six distinct, vibrant colors.
// ---------------------------------------------------------------------------

const PALETTE = [
  0xff6b6b, // coral red
  0x4ecdc4, // teal
  0x45b7d1, // sky blue
  0xf7dc6f, // warm yellow
  0xbb8fce, // soft purple
  0x82e0aa, // mint green
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CubeSpinnerVariant = "dark" | "light" | "rainbow";

export interface CubeSpinnerProps {
  /** CSS pixel size of the canvas (square). @default 28 */
  size?: number;
  /** Color scheme. @default "dark" */
  variant?: CubeSpinnerVariant;
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

function createEdgesGeometry(): THREE.EdgesGeometry {
  const box = new THREE.BoxGeometry(1, 1, 1);
  const edges = new THREE.EdgesGeometry(box);
  box.dispose();
  return edges;
}

function createFaceGeometry(): THREE.BoxGeometry {
  return new THREE.BoxGeometry(0.999, 0.999, 0.999);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CubeSpinner({ size = 28, variant = "dark" }: CubeSpinnerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let disposed = false;
    let rafId = 0;

    // Resolution: 2× CSS size for crisp edges on all displays.
    const res = size * 2;
    canvas.width = res;
    canvas.height = res;

    // -- Renderer (async init) ---------------------------------------------

    const renderer = new THREE.WebGPURenderer({
      canvas,
      antialias: true,
      alpha: true,
    });
    renderer.setSize(res, res, false);
    renderer.setClearColor(0x000000, 0);

    // -- Scene -------------------------------------------------------------

    const scene = new THREE.Scene();

    const frustum = 0.9;
    const camera = new THREE.OrthographicCamera(
      -frustum,
      frustum,
      frustum,
      -frustum,
      0.1,
      10,
    );
    camera.position.set(0, 0, 3);
    camera.lookAt(0, 0, 0);

    // -- Material per variant ----------------------------------------------

    let material: THREE.LineBasicNodeMaterial;

    if (variant === "rainbow") {
      material = new THREE.LineBasicNodeMaterial();
      const t = time.mul(0.8);
      material.colorNode = hue(tslColor(0xff5555), t);
    } else {
      const hex = variant === "light" ? 0x222228 : 0xffffff;
      material = new THREE.LineBasicNodeMaterial();
      material.colorNode = tslColor(hex);
      material.opacity = variant === "light" ? 0.6 : 0.5;
      material.transparent = true;
    }
    material.linewidth = 3;

    const edgesGeo = createEdgesGeometry();
    const cube = new THREE.LineSegments(edgesGeo, material);
    scene.add(cube);

    // -- Discrete 45° rotation stepping ------------------------------------
    //
    // The cube tumbles in smooth-interpolated 45° steps around randomly
    // chosen world axes.  Each step picks a direction that is not the
    // reverse of the previous one, so the cube never immediately undoes
    // its last move.
    //
    // Axes are paired: index i and i^1 are opposites (+X / -X, etc.).
    const STEP_AXES = [
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(-1, 0, 0),
      new THREE.Vector3(0, 1, 0),
      new THREE.Vector3(0, -1, 0),
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(0, 0, -1),
    ];
    const STEP_ANGLE = Math.PI / 4; // 45°
    const STEP_DURATION = 0.35; // seconds of interpolation per step
    const STEP_PAUSE = 0.1; // brief rest between steps

    // Bake the initial tilt (top face visible) into the starting quaternion.
    const qFrom = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(0.4, 0.0, 0),
    );
    const qTo = qFrom.clone();
    cube.quaternion.copy(qFrom);

    let lastAxisIdx = -1; // no previous step yet
    let stepT = 1; // 1 = step complete, ready for pause/next
    let stepPauseT = 0;
    let inStepPause = false;

    function pickAxis(lastIdx: number): number {
      const reverseIdx = lastIdx < 0 ? -1 : lastIdx ^ 1;
      const choices = STEP_AXES.map((_, i) => i).filter(
        (i) => i !== reverseIdx,
      );
      return choices[Math.floor(Math.random() * choices.length)];
    }

    // -- Colored faces with pulsing radial gradients -----------------------
    //
    // Each face gets a center-outward radial gradient whose boundary breathes
    // at a randomly-assigned speed and amplitude, giving each face its own
    // distinct life while staying cohesive.

    // Six animation personalities — speed (rad/s) × pulse amplitude.
    const ANIM_STYLES = [
      { pulseSpeed: 0.7, pulseAmp: 0.25 }, // slow deep breathe
      { pulseSpeed: 2.1, pulseAmp: 0.1 }, // fast subtle flutter
      { pulseSpeed: 1.3, pulseAmp: 0.18 }, // medium
      { pulseSpeed: 0.45, pulseAmp: 0.3 }, // very slow swell
      { pulseSpeed: 1.75, pulseAmp: 0.14 }, // fast medium
      { pulseSpeed: 1.05, pulseAmp: 0.22 }, // medium deep
    ];

    const faceGradients = Array.from({ length: 6 }, () => {
      const c1 = uniform(new THREE.Color(0x000000));
      const c2 = uniform(new THREE.Color(0x000000));
      return { color1: c1, color2: c2 };
    });

    const faceMats = faceGradients.map((grad, i) => {
      const m = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      const style = ANIM_STYLES[i];
      const phaseOffset = i * 1.1; // stagger so faces don't pulse in unison

      // Radial distance from UV centre (0.5, 0.5), normalised so the
      // face corner lands near 1.0 (multiply by √2).
      const uvX = uv().x.sub(0.5);
      const uvY = uv().y.sub(0.5);
      const dist = uvX.mul(uvX).add(uvY.mul(uvY)).sqrt().mul(Math.SQRT2);

      // Breathing pulse shifts the gradient boundary in and out over time.
      const pulse = sin(time.mul(style.pulseSpeed).add(phaseOffset)).mul(
        style.pulseAmp,
      );
      const t = dist.add(pulse).clamp(0, 1);

      m.colorNode = mix(grad.color1, grad.color2, t);
      return m;
    });

    const faceGeo = createFaceGeometry();
    const faceMesh = new THREE.Mesh(faceGeo, faceMats);
    cube.add(faceMesh); // inherits wireframe rotation

    const filled: boolean[] = new Array(6).fill(false);
    const fadeProgress: number[] = new Array(6).fill(0); // 0→1 over FADE_IN seconds
    let fillTimer = 0;
    const FILL_INTERVAL = 0.22; // seconds between face fills
    const FADE_IN = 0.2; // seconds for opacity to reach full
    const PAUSE_AFTER_FULL = 0.45; // pause once all 6 are lit
    let pauseTimer = 0;
    let paused = false;

    // -- Animation loop (starts after renderer.init()) ---------------------

    const clock = new THREE.Clock();

    function animate() {
      if (disposed) return;
      rafId = requestAnimationFrame(animate);

      const dt = clock.getDelta();

      // Discrete 45° step machine
      if (stepT >= 1 && !inStepPause) {
        inStepPause = true;
        stepPauseT = 0;
      }
      if (inStepPause) {
        stepPauseT += dt;
        if (stepPauseT >= STEP_PAUSE) {
          inStepPause = false;
          lastAxisIdx = pickAxis(lastAxisIdx);
          const delta = new THREE.Quaternion().setFromAxisAngle(
            STEP_AXES[lastAxisIdx],
            STEP_ANGLE,
          );
          qFrom.copy(qTo);
          qTo.copy(qFrom).premultiply(delta); // world-space rotation
          stepT = 0;
        }
      }
      if (!inStepPause) {
        stepT = Math.min(1, stepT + dt / STEP_DURATION);
        const ease = stepT * stepT * (3 - 2 * stepT); // smoothstep
        cube.quaternion.slerpQuaternions(qFrom, qTo, ease);
      }

      // Advance fade-in for each filled face
      for (let i = 0; i < 6; i++) {
        if (filled[i] && fadeProgress[i] < 1) {
          fadeProgress[i] = Math.min(1, fadeProgress[i] + dt / FADE_IN);
          faceMats[i].opacity = fadeProgress[i];
        }
      }

      // Face-fill cycle
      if (paused) {
        pauseTimer += dt;
        if (pauseTimer >= PAUSE_AFTER_FULL) {
          paused = false;
          pauseTimer = 0;
          for (let i = 0; i < 6; i++) {
            filled[i] = false;
            fadeProgress[i] = 0;
            faceMats[i].opacity = 0;
          }
        }
      } else {
        fillTimer += dt;
        if (fillTimer >= FILL_INTERVAL) {
          fillTimer -= FILL_INTERVAL;
          const empty = filled
            .map((f, i) => (f ? -1 : i))
            .filter((i) => i >= 0);

          if (empty.length === 0) {
            paused = true;
          } else {
            const idx = empty[Math.floor(Math.random() * empty.length)];
            filled[idx] = true;
            fadeProgress[idx] = 0;
            const c1Idx = Math.floor(Math.random() * PALETTE.length);
            let c2Idx = Math.floor(Math.random() * (PALETTE.length - 1));
            if (c2Idx >= c1Idx) c2Idx++;
            faceGradients[idx].color1.value.setHex(PALETTE[c1Idx]);
            faceGradients[idx].color2.value.setHex(PALETTE[c2Idx]);
            // opacity starts at 0 and is ramped up by the fade loop above
          }
        }
      }

      renderer.renderAsync(scene, camera);
    }

    renderer.init().then(() => {
      if (!disposed) animate();
    });

    // -- Cleanup -----------------------------------------------------------

    return () => {
      disposed = true;
      cancelAnimationFrame(rafId);
      material.dispose();
      edgesGeo.dispose();
      faceGeo.dispose();
      for (const m of faceMats) m.dispose();
      renderer.dispose();
    };
  }, [size, variant]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: size,
        height: size,
        display: "block",
        pointerEvents: "none",
      }}
    />
  );
}
